import { useQuery } from '@apollo/client/react';
import React from 'react';

import { useViewerCompany } from './containers/hooks';
import { companyUsers, USERS_TABLE_PAGE_SIZE } from './graphql';
import {
  CompanyUsersQuery as CompanyUsersResult,
  CompanyUsersQueryVariables,
  getNodes,
  loadMoreFromConnection,
} from '../lib/graphql';

export function useUsersInCompany(search?: string) {
  const {
    company: { company_id },
  } = useViewerCompany();
  const result = useQuery<CompanyUsersResult, CompanyUsersQueryVariables>(companyUsers, {
    variables: { companyId: company_id, first: USERS_TABLE_PAGE_SIZE, search },
  });

  const loadNextPage = React.useCallback(async () => {
    await loadMoreFromConnection(result, ['viewer', 'company', 'users', 'users'], USERS_TABLE_PAGE_SIZE);
  }, [result]);

  if (result.error !== undefined) {
    throw result.error;
  }

  const isLoading = result.loading;
  const users = getNodes(result.data?.viewer?.company?.users.users);

  return {
    isLoading,
    queryResult: users,
    countResult: result.data?.viewer?.company?.users.count ?? 0,
    isPresentOnTpPlatform: result.data?.viewer?.company?.isPresentOnTransporeonPlatform ?? false,
    isShipper: result.data?.viewer?.company?.is_shipper ?? false,
    hasNextPage: result.data?.viewer?.company?.users.users.pageInfo.hasNextPage ?? false,
    loadNextPage,
  };
}

export function useUserSearch(searchQuery: string | undefined) {
  const queryData = useUsersInCompany(searchQuery);
  const [hasEverLoadedData, setHasEverLoadedData] = React.useState(false);
  const [staleData, setStaleData] = React.useState<{
    users: typeof queryData.queryResult;
    hasNextPage: boolean;
    loadNextPage: () => Promise<void>;
  }>({
    users: [],
    hasNextPage: false,
    loadNextPage: () => Promise.resolve(),
  });

  const prevLoadingRef = React.useRef<boolean>(true);
  const prevResultLengthRef = React.useRef<number>(-1);

  React.useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    const currentResultLength = queryData.queryResult?.length ?? -1;
    const resultLengthChanged = prevResultLengthRef.current !== currentResultLength;

    // Only update when:
    // 1. Loading just finished (was loading, now not loading)
    // 2. AND we have defined results (even if empty array)
    // 3. AND the result length actually changed (to prevent infinite loops with same empty arrays)
    if (wasLoading && !queryData.isLoading && queryData.queryResult !== undefined && resultLengthChanged) {
      setHasEverLoadedData(true);
      setStaleData({
        users: queryData.queryResult,
        hasNextPage: queryData.hasNextPage,
        loadNextPage: queryData.loadNextPage,
      });

      prevResultLengthRef.current = currentResultLength;
    }

    prevLoadingRef.current = queryData.isLoading;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryData.isLoading, queryData.queryResult, queryData.hasNextPage]);

  const displayData = React.useMemo(() => {
    if (queryData.isLoading) {
      return {
        users: staleData.users,
        hasNextPage: staleData.hasNextPage,
        loadNextPage: staleData.loadNextPage,
      };
    }
    return {
      users: queryData.queryResult || staleData.users,
      hasNextPage: queryData.hasNextPage,
      loadNextPage: queryData.loadNextPage,
    };
  }, [
    queryData.isLoading,
    queryData.queryResult,
    queryData.hasNextPage,
    queryData.loadNextPage,
    staleData.users,
    staleData.hasNextPage,
    staleData.loadNextPage,
  ]);

  const shouldShowInitialLoading = React.useMemo(
    () => queryData.isLoading && !hasEverLoadedData,
    [queryData.isLoading, hasEverLoadedData],
  );

  return React.useMemo(
    () => ({
      isLoading: queryData.isLoading,
      queryResult: queryData.queryResult,
      countResult: queryData.countResult,
      isPresentOnTpPlatform: queryData.isPresentOnTpPlatform,
      isShipper: queryData.isShipper,
      hasNextPage: queryData.hasNextPage,
      loadNextPage: queryData.loadNextPage,
      displayData,
      hasEverLoadedData,
      shouldShowInitialLoading,
    }),
    [
      queryData.isLoading,
      queryData.queryResult,
      queryData.countResult,
      queryData.isPresentOnTpPlatform,
      queryData.isShipper,
      queryData.hasNextPage,
      queryData.loadNextPage,
      displayData,
      hasEverLoadedData,
      shouldShowInitialLoading,
    ],
  );
}
